import { COUNTRY_CONFIG } from "../config/countries";
import type {
  ProviderCondition,
  ResolvedProvider,
} from "../config/energyProvider";
import { STRINGS } from "@/i18n";
import { cn } from "@/lib/utils";
import { formatCoefficient, formatEuro } from "@/utils/format";
import {
  computeDisplayedFeedIn,
  type DisplayedMonthlyEstimate,
} from "@/utils/priceCalculations";
import { describeCondition } from "@/utils/providerConditions";
import { ConditionChip } from "./ProviderChips";

/** Decimals of every per-kWh amount; must match the table. */
export const PER_KWH_DECIMALS = 4;

const NBSP = " ";
const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

interface ProviderDetailsProps {
  provider: ResolvedProvider;
  /** Conditions in force today. */
  conditions: ProviderCondition[];
  /** The displayed estimate of the row; null when no market price is known. */
  estimate: DisplayedMonthlyEstimate | null;
  /** Spot price the estimate is based on, reused for the feed-in example. */
  spotPrice?: number;
  includeTaxes: boolean;
  /**
   * True for the provider selected for the page: its row carries the highlight colour, so
   * the panel is white; in any other row the panel is grey.
   */
  highlighted: boolean;
  /** Show the content in its own bordered panel (desktop) or straight in the card (phones). */
  framed?: boolean;
}

/**
 * The expanded part of a provider row, in three columns from the tablet width: the
 * written-out sum, the feed-in revenue (and product), and the conditions with their
 * explanations.
 */
export function ProviderDetails({
  provider,
  conditions,
  estimate,
  spotPrice,
  includeTaxes,
  highlighted,
  framed = true,
}: ProviderDetailsProps) {
  const t = STRINGS.providers;
  const euroPerKwh = (value: number) => formatEuro(value, PER_KWH_DECIMALS);
  const feedInVatPercent = Math.round(COUNTRY_CONFIG.feedInVatRate * 100);
  // "Berekening maandlast" as a running calculation, one line per step. A factor applies
  // to the market price only, so with a factor the taxes get their own line after it.
  type CalculationLine = { label: string; value: string; kind?: "subtotal" | "total" };
  const calculation: CalculationLine[] = [];
  if (estimate) {
    const perKwh = (value: number) => `${euroPerKwh(value)} / kWh`;
    const showFactor =
      COUNTRY_CONFIG.country === "be" || estimate.spotCoefficient !== 1;
    if (showFactor) {
      calculation.push(
        {
          label: includeTaxes ? t.calcMarketInclVat : t.calcMarketExclTaxes,
          value: perKwh(estimate.marketPrice),
        },
        {
          label: t.calcFactor(provider.name),
          value: `×${formatCoefficient(estimate.spotCoefficient)}`,
        }
      );
      if (includeTaxes) {
        calculation.push({
          label: COUNTRY_CONFIG.energyTaxLabel,
          value: `+ ${perKwh(estimate.energyTax)}`,
        });
      }
    } else {
      calculation.push({
        label: includeTaxes ? t.calcMarketInclTaxes : t.calcMarketExclTaxes,
        value: perKwh(estimate.marketPrice + estimate.energyTax),
      });
    }
    calculation.push(
      { label: t.calcMarkup(provider.name), value: `+ ${perKwh(estimate.markup)}` },
      { label: t.calcUsage, value: `× ${estimate.usageKwh}${NBSP}kWh` },
      {
        label: t.calcEnergyCost,
        value: formatEuro(estimate.energyCost, 2),
        kind: "subtotal",
      },
      { label: t.calcMonthlyFee, value: `+ ${formatEuro(estimate.monthlyFee, 2)}` },
      { label: t.monthlyEstimate, value: formatEuro(estimate.total, 2), kind: "total" }
    );
  }

  const feedIn =
    provider.feedInPerKwhChargeExcludingVat === undefined
      ? null
      : computeDisplayedFeedIn({
          spotPrice,
          feedInSpotCoefficient: provider.feedInSpotCoefficient,
          feedInPerKwhCharge: provider.feedInPerKwhChargeExcludingVat,
          vatRate: COUNTRY_CONFIG.feedInVatRate,
          perKwhDecimals: PER_KWH_DECIMALS,
        });
  const feedInFormula = (() => {
    if (!feedIn) return null;
    const coefficient =
      feedIn.spotCoefficient !== undefined
        ? `${formatCoefficient(feedIn.spotCoefficient)} × `
        : "";
    const fee =
      feedIn.fee > 0
        ? ` − ${euroPerKwh(feedIn.fee)}`
        : feedIn.fee < 0
          ? ` + ${euroPerKwh(-feedIn.fee)}`
          : "";
    return capitalize(
      coefficient === "" && fee === ""
        ? `${t.feedInMarketPrice}, ${t.feedInNoFee}`
        : `${coefficient}${t.feedInMarketPrice}${fee} ${t.perKwh}`
    );
  })();
  const feedInNote =
    feedIn &&
    [
      `${capitalize(feedInVatPercent > 0 ? t.termInclVat(feedInVatPercent) : t.feedInVatExempt)}.`,
      ...(feedIn.marketPrice !== null && feedIn.revenue !== null
        ? [t.feedInExample(euroPerKwh(feedIn.marketPrice), euroPerKwh(feedIn.revenue))]
        : []),
    ].join(" ");

  // Typography of the panel: fixed light-mode colours (#6B7280 / #111827) with the theme
  // colours as dark-mode fallback.
  const headingClass =
    "text-[13px] font-semibold leading-5 text-[#6B7280] dark:text-muted-foreground";
  const valueClass =
    "mt-2 text-sm font-normal tabular-nums text-[#111827] dark:text-foreground";
  const textClass = "mt-2 text-sm font-normal leading-relaxed text-[#111827] dark:text-foreground";
  const conditionTextClass =
    "mt-1.5 text-[13px] font-normal leading-5 text-[#111827] dark:text-foreground";

  return (
    <div
      className={cn(
        "text-foreground",
        framed && [
          "rounded-lg border border-border p-4 md:p-5",
          highlighted ? "bg-card" : "bg-muted/50",
        ]
      )}
      data-testid="provider-details"
    >
      <div className="grid gap-6 md:grid-cols-3 md:gap-8">
        <section aria-label={t.calculationTitle}>
          <h4 className={headingClass}>{t.calculationTitle}</h4>
          {calculation.length > 0 ? (
            <dl className="mt-2 text-sm" data-testid="provider-calculation">
              {calculation.map((line) => (
                <div
                  key={line.label}
                  data-calculation-line
                  className={cn(
                    "flex items-baseline justify-between gap-4 leading-6",
                    line.kind === "subtotal" &&
                      "mt-1.5 border-t border-border pt-1.5",
                    line.kind === "total" &&
                      "mt-1.5 border-t border-[#111827] pt-1.5 text-[15px] font-semibold dark:border-foreground"
                  )}
                >
                  <dt
                    className={cn(
                      "min-w-0",
                      line.kind
                        ? "text-[#111827] dark:text-foreground"
                        : "text-[#6B7280] dark:text-muted-foreground"
                    )}
                  >
                    {line.label}
                  </dt>
                  <dd className="whitespace-nowrap text-right tabular-nums text-[#111827] dark:text-foreground">
                    {line.value}
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className={textClass}>{t.noEstimate}</p>
          )}
        </section>

        <section aria-label={t.feedInTitle}>
          <h4 className={headingClass}>{t.feedInTitle}</h4>
          {feedInFormula ? (
            <>
              <p className={valueClass}>{feedInFormula}</p>
              <p className={textClass}>{feedInNote}</p>
            </>
          ) : (
            <p className={textClass}>{t.feedInUnknown}</p>
          )}
          {provider.feedInComment && (
            <p className={textClass}>{provider.feedInComment}</p>
          )}
          {provider.product && (
            <>
              <h4 className={`${headingClass} mt-5`}>{t.productLabel}</h4>
              <p className={valueClass}>{provider.product}</p>
            </>
          )}
        </section>

        {conditions.length > 0 && (
          <section aria-label={t.conditionsTitle}>
            <h4 className={headingClass}>{t.conditionsTitle}</h4>
            <ul className="mt-2 space-y-4">
              {conditions.map((condition, index) => {
                const description = describeCondition(condition);
                return (
                  <li key={index}>
                    <ConditionChip description={description} />
                    <p className={conditionTextClass}>{description.detail}</p>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
